"use client";

import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Square,
  Download,
  Calendar,
} from "lucide-react";
import PermissionGuard from "@/components/PermissionGuard";
import { PermissionSubject } from "@/utils/permissions";
import { useMovieImportStore } from "@/stores/movieImportStore";
import { SyncStatusEnum, SyncTypeEnum, SyncLog } from "@/types/kkmovie";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { toast } from "sonner";

const statusConfig: Record<
  SyncStatusEnum,
  { label: string; icon: any; class: string }
> = {
  [SyncStatusEnum.PENDING]: {
    label: "Chờ xử lý",
    icon: Clock,
    class: "bg-gray-100 text-gray-800",
  },
  [SyncStatusEnum.IN_PROGRESS]: {
    label: "Đang chạy",
    icon: RefreshCw,
    class: "bg-blue-100 text-blue-800",
  },
  [SyncStatusEnum.SUCCESS]: {
    label: "Thành công",
    icon: CheckCircle,
    class: "bg-green-100 text-green-800",
  },
  [SyncStatusEnum.FAILED]: {
    label: "Thất bại",
    icon: XCircle,
    class: "bg-red-100 text-red-800",
  },
  [SyncStatusEnum.PARTIAL]: {
    label: "Một phần",
    icon: AlertCircle,
    class: "bg-yellow-100 text-yellow-800",
  },
};

const typeLabels: Record<string, string> = {
  [SyncTypeEnum.MANUAL]: "Thủ công",
  [SyncTypeEnum.SCHEDULED]: "Tự động",
  [SyncTypeEnum.SINGLE]: "Phim đơn",
  [SyncTypeEnum.IMPORT_ALL]: "Import tất cả",
  [SyncTypeEnum.DAILY_NEW_IMPORT]: "Import mới hàng ngày",
};

function formatDate(dateString: string) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("vi-VN");
}

function formatDuration(startedAt: string, completedAt?: string) {
  if (!startedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const duration = Math.floor((end - start) / 1000);

  if (duration < 60) return `${duration}s`;
  if (duration < 3600)
    return `${Math.floor(duration / 60)}m ${duration % 60}s`;
  return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
}

export default function MovieImportPage() {
  const navigate = useNavigate();
  const {
    importStatus,
    importHistory,
    historyPagination,
    isLoading,
    isImporting,
    error,
    fetchImportStatus,
    fetchImportHistory,
    triggerImportAll,
    triggerDailyImport,
    stopImport,
  } = useMovieImportStore();

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedSource, setSelectedSource] = useState<"KKphim" | "OPhim">(
    "KKphim"
  );

  useEffect(() => {
    fetchImportStatus();
    fetchImportHistory();
  }, [fetchImportStatus, fetchImportHistory]);

  // Auto refresh when import is running
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (
      autoRefresh ||
      importStatus?.isImportAllRunning ||
      importStatus?.isDailyImportRunning
    ) {
      interval = setInterval(() => {
        fetchImportStatus();
        fetchImportHistory();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    autoRefresh,
    importStatus?.isImportAllRunning,
    importStatus?.isDailyImportRunning,
    fetchImportStatus,
    fetchImportHistory,
  ]);

  const handleImportAll = async () => {
    const success = await triggerImportAll(selectedSource);
    if (success) {
      toast.success(
        `Đã bắt đầu import tất cả phim từ ${selectedSource}`
      );
      setAutoRefresh(true);
    } else {
      toast.error(error || "Không thể bắt đầu import");
    }
  };

  const handleDailyImport = async () => {
    const success = await triggerDailyImport();
    if (success) {
      toast.success("Đã bắt đầu import phim mới hàng ngày");
      setAutoRefresh(true);
    } else {
      toast.error(error || "Không thể bắt đầu daily import");
    }
  };

  const handleStop = async () => {
    const success = await stopImport();
    if (success) {
      toast.success("Đã gửi lệnh dừng import");
    } else {
      toast.error(error || "Không thể dừng import");
    }
  };

  const handleRefresh = () => {
    fetchImportStatus();
    fetchImportHistory();
  };

  const handleHistoryPage = (page: number) => {
    fetchImportHistory(page, historyPagination.limit);
  };

  const isRunning =
    importStatus?.isImportAllRunning || importStatus?.isDailyImportRunning;

  // Get the latest running log for progress display
  const runningLog =
    importStatus?.latestImportAll?.status === SyncStatusEnum.IN_PROGRESS
      ? importStatus.latestImportAll
      : importStatus?.latestDailyImport?.status === SyncStatusEnum.IN_PROGRESS
        ? importStatus.latestDailyImport
        : null;

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.KKMOVIES)}
              className="hover:cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Quay lại
            </Button>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
                Import Phim
              </h1>
              <p className="mt-1 text-gray-500">
                Import tất cả phim & cronjob import phim mới hàng ngày
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2 hover:cursor-pointer"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Làm mới
            </Button>
            {isRunning && (
              <PermissionGuard
                subject={PermissionSubject.ARTICLE}
                action="update"
              >
                <Button
                  onClick={handleStop}
                  variant="outline"
                  className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:cursor-pointer"
                >
                  <Square className="h-4 w-4" />
                  Dừng Import
                </Button>
              </PermissionGuard>
            )}
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Import All Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Download className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Import tất cả phim
                </h3>
                <p className="text-sm text-gray-500">
                  Duyệt tất cả pages từ API, import phim chưa có
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <select
                value={selectedSource}
                onChange={(e) =>
                  setSelectedSource(e.target.value as "KKphim" | "OPhim")
                }
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
              >
                <option value="KKphim">KKPhim (phimapi.com)</option>
                <option value="OPhim">OPhim (ophim1.com)</option>
              </select>
              <PermissionGuard
                subject={PermissionSubject.ARTICLE}
                action="create"
              >
                <Button
                  onClick={handleImportAll}
                  disabled={isImporting || isRunning}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
                >
                  <Play
                    className={`h-4 w-4 ${isImporting ? "animate-pulse" : ""}`}
                  />
                  {isRunning ? "Đang chạy..." : "Bắt đầu"}
                </Button>
              </PermissionGuard>
            </div>
            {importStatus?.latestImportAll && (
              <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
                Lần cuối:{" "}
                {formatDate(
                  importStatus.latestImportAll.completedAt ||
                    importStatus.latestImportAll.startedAt
                )}{" "}
                - {importStatus.latestImportAll.newMoviesCount || 0} phim mới
              </div>
            )}
          </div>

          {/* Daily Import Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Import phim mới hàng ngày
                </h3>
                <p className="text-sm text-gray-500">
                  Cronjob 3h sáng - check 3 pages mới nhất từ cả 2 nguồn
                </p>
              </div>
            </div>
            <PermissionGuard
              subject={PermissionSubject.ARTICLE}
              action="create"
            >
              <Button
                onClick={handleDailyImport}
                disabled={isImporting || importStatus?.isDailyImportRunning}
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
              >
                <Play
                  className={`h-4 w-4 ${importStatus?.isDailyImportRunning ? "animate-pulse" : ""}`}
                />
                {importStatus?.isDailyImportRunning
                  ? "Đang chạy..."
                  : "Trigger thủ công"}
              </Button>
            </PermissionGuard>
            {importStatus?.latestDailyImport && (
              <div className="text-xs text-gray-500 border-t border-gray-100 pt-3 mt-4">
                Lần cuối:{" "}
                {formatDate(
                  importStatus.latestDailyImport.completedAt ||
                    importStatus.latestDailyImport.startedAt
                )}{" "}
                - {importStatus.latestDailyImport.newMoviesCount || 0} phim mới
              </div>
            )}
          </div>
        </div>

        {/* Progress Card (when running) */}
        {isRunning && runningLog && (
          <div className="bg-white rounded-xl shadow-lg p-6 border border-emerald-200 mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Tiến trình Import
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-emerald-600 animate-spin" />
                <span className="text-lg font-semibold text-emerald-600">
                  Đang import {runningLog.source || ""}...
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Phim mới:</span>
                  <span className="ml-2 font-semibold text-green-600">
                    {runningLog.newMoviesCount || 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Bỏ qua:</span>
                  <span className="ml-2 font-semibold text-gray-600">
                    {runningLog.skippedCount || 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Lỗi:</span>
                  <span className="ml-2 font-semibold text-red-600">
                    {runningLog.failedCount || 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Trang:</span>
                  <span className="ml-2 font-semibold">
                    {runningLog.currentPage || 0}/{runningLog.totalPages || "?"}
                  </span>
                </div>
              </div>
              {runningLog.totalPages > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(runningLog.currentPage / runningLog.totalPages) * 100}%`,
                    }}
                  />
                </div>
              )}
              <div className="text-sm text-gray-500">
                Thời gian: {formatDuration(runningLog.startedAt)}
              </div>
            </div>
          </div>
        )}

        {/* Auto Refresh Toggle */}
        <div className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="autoRefreshImport"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <label
            htmlFor="autoRefreshImport"
            className="text-sm text-gray-600"
          >
            Tự động làm mới mỗi 5 giây
          </label>
        </div>

        {/* Import History */}
        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              Lịch sử Import
            </h2>
          </div>

          {isLoading && importHistory.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Thời gian
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Loại
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Nguồn
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Phim mới
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Bỏ qua/Lỗi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Thời lượng
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {importHistory.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <Download className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-600 font-medium">
                              Chưa có lịch sử import
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              Bấm "Bắt đầu" để import phim từ nguồn bên ngoài
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      importHistory.map((log: SyncLog) => {
                        const StatusIcon = statusConfig[log.status]?.icon;
                        return (
                          <tr
                            key={log._id}
                            className="transition-all hover:bg-gray-50/60"
                          >
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {formatDate(log.startedAt)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {typeLabels[log.type] || log.type}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  log.source === "OPhim"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {log.source || "-"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusConfig[log.status]?.class || "bg-gray-100 text-gray-800"}`}
                              >
                                {StatusIcon && (
                                  <StatusIcon
                                    className={`h-3 w-3 ${log.status === SyncStatusEnum.IN_PROGRESS ? "animate-spin" : ""}`}
                                  />
                                )}
                                {statusConfig[log.status]?.label || log.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-green-600">
                              +{log.newMoviesCount || 0}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {log.skippedCount || 0} bỏ qua
                              {log.failedCount > 0 && (
                                <span className="ml-2 text-red-600">
                                  ({log.failedCount} lỗi)
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatDuration(log.startedAt, log.completedAt)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {historyPagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                    <div>
                      <p className="text-sm text-gray-700">
                        Trang{" "}
                        <span className="font-medium">
                          {historyPagination.page}
                        </span>{" "}
                        / {historyPagination.totalPages}
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          onClick={() =>
                            handleHistoryPage(
                              Math.max(1, historyPagination.page - 1)
                            )
                          }
                          disabled={historyPagination.page === 1}
                          variant="outline"
                          size="sm"
                          className="rounded-l-md hover:cursor-pointer"
                        >
                          Trước
                        </Button>
                        <Button
                          onClick={() =>
                            handleHistoryPage(
                              Math.min(
                                historyPagination.totalPages,
                                historyPagination.page + 1
                              )
                            )
                          }
                          disabled={
                            historyPagination.page ===
                            historyPagination.totalPages
                          }
                          variant="outline"
                          size="sm"
                          className="rounded-r-md hover:cursor-pointer"
                        >
                          Sau
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
